import * as jose from "jose";
import Cookies from "js-cookie";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { Navigate, Outlet } from "react-router-dom";

import instance from "../api/axios";
import { refreshToken } from "../api/request";

interface AuthProps {
  user: string | null;
  idClasse?: string | null;
  isLoading: boolean;

  cleanCookies?: () => void;
  isBeneylu?: () => boolean;
  isEdifice?: () => boolean;
  isTeacher?: () => boolean;
  logout?: () => void;
  refresh?: (token?: string | null) => void;
  setIDClasse?: (idClasse: string) => void;
}

const AuthContext = createContext<AuthProps>({
  user: null,
  isLoading: true,
});
export default AuthContext;

export const useAuth = () => useContext(AuthContext);

const BASE_URL_AUTH =
  process.env.NODE_ENV === "production"
    ? "https://auth.nabook.co/signin?redirect_to=https://webapp.nabook.co/"
    : "http://localhost:3003/signin?redirect_to=http://localhost:3001";

const getQueryParam = (name: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
};

export const AuthContextProvider = ({ children }: { children: any }) => {
  const [user, setUser] = useState<string | null>(null);
  const [idClasse, setClasse] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const cleanCookies = useCallback(() => {
    // console.log("🧹-cleanCookies", Cookies.get("user"));
    // Cookies.remove("user", { path: "/", domain: ".nabook.co" });
    // Cookies.remove("user", { path: "/", domain: "webapp.nabook.co" });
    Cookies.remove("user", {
      sameSite: "None",
      secure: true,
    });
    // console.log("🧹-cleanCookies#after", Cookies.get("user"));
    setUser(null);
    setClasse(null);
    instance.defaults.headers.common["Authorization"] = ``;
  }, []);

  const logout = useCallback(() => {
    cleanCookies();
    window.location.replace(BASE_URL_AUTH);
  }, [cleanCookies]);

  const refresh = useCallback(
    async (token?: string | null) => {
      let newToken = null;
      const userProfile = Cookies.get("user");

      if (!userProfile && !token) {
        console.log("⏰-logout");
        logout();
        return;
      }

      if (token) {
        const decoded: { exp?: number } = jose.decodeJwt(token);
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          // Obligé de faire ça car le token n'est pas tout le temps dans le header à la nouvelle connexion
          instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          const t = await refreshToken();
          if (!t || !t.isOk) {
            console.log("⏰-logout-token");
            logout();
          }
          newToken = t.cookie;
        } else newToken = token;
      }

      if (userProfile && !token) {
        const decoded: { exp?: number } = jose.decodeJwt(userProfile);
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          // Obligé de faire ça car le token n'est pas tout le temps dans le header à la nouvelle connexion
          instance.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${userProfile}`;

          const t = await refreshToken();
          if (!t || !t.isOk) {
            console.log("⏰-logout-userProfile");
            logout();
          }
          newToken = t.cookie;
        } else newToken = userProfile;
      }

      if (newToken) {
        Cookies.set("user", newToken, {
          sameSite: "None",
          secure: true,
        });
        setUser(newToken);
        instance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${newToken}`;

        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        console.log("URL cleaned and state set.");
      }

      setIsLoading(false);
    },
    [logout]
  );

  const isEdifice = () => {
    const userProfile = user || Cookies.get("user");
    if (!userProfile) return false;
    const decoded: { type?: string } = jose.decodeJwt(userProfile);
    if (decoded.type && decoded.type === "edifice") return true;
    return false;
  };

  const isBeneylu = () => {
    const userProfile = user || Cookies.get("user");
    if (!userProfile) return false;
    const decoded: { type?: string } = jose.decodeJwt(userProfile);
    if (decoded.type && decoded.type === "beneylu") return true;
    return false;
  };

  const isTeacher = () => {
    const userProfile = user || Cookies.get("user");
    if (!userProfile) return false;
    const decoded: { idTeacher?: string } = jose.decodeJwt(userProfile);
    console.log("🚀 ~ isTeacher ~ decoded:", decoded)
    if (decoded.idTeacher) return true;
    return false;
  };

  const setIDClasse = (idClasse: string) => {
    setClasse(idClasse);
  };

  useEffect(() => {
    // may fix token reload bug in edifice
    if (window.self === window.top) {
      const userProfile = Cookies.get("user");
      if (userProfile) {
        refresh(userProfile); 
        return;
      }
    }

    // Fix le soucis des cookies partitionnées (non partagées) dans les iframes.
    // Attention, pas du tout sécurisé, il faudrait utiliser un id de cookie à récupérer côté backend
    // Generate a Handover Key: For better security, instead of using the main session ID directly in the URL, you can ask your backend to generate a short-lived, single-use handover token linked to the user's active session.
    // TOOD: fix cette merde
    const sessionTokenFromUrl = getQueryParam('session_token');
    
    if (sessionTokenFromUrl) {
      refresh(sessionTokenFromUrl);
    } else {
      setIsLoading(false);
      console.log("Skipped UEF");
    }
  }, [refresh]);

  return (
    <>
      <AuthContext.Provider
        value={{
          idClasse,
          isLoading,
          user,
          cleanCookies,
          isBeneylu,
          isEdifice,
          isTeacher,
          logout,
          refresh,
          setIDClasse,
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};

export const RequireAuth = () => {
  const { user, isLoading } = useAuth();

  if (isLoading) return null;

  if (!user && !isLoading)
    return <Navigate to={{ pathname: "/logout" }} replace />;

  return <Outlet />;
};
